import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useMemo,
  useCallback
} from 'react';
import { useDispatch } from 'react-redux';
import { createAlert } from 'features/alert/alertSlice';
import {
  setConnecting,
  setReady,
  setLoading,
  setPlaying,
  setPlay,
  setDuration,
  setError,
  resetAudioState
} from 'features/audio/audioSlice';
// Type-only: the 128 KB standardized-audio-context engine is dynamically
// imported on first playback (see the AudioContext setup effect below) so it
// stays out of the initial bundle / homepage LCP path.
import type { AudioContext } from 'standardized-audio-context';
import dayjs from 'utilities/dayjs';
import {
  Broadcast,
  setAudioEndedTimestamp
} from 'features/broadcast/broadcastsSlice';
import { checkAndPlayAudio, fetchAudioAbort } from 'features/audio/audioThunks';
import {
  currentBroadcastFinishedSelector,
  currentEventBroadcastSelector
} from 'features/broadcast/selectors';
import { useLocalStorage } from 'usehooks-ts';
import { isSafari } from 'react-device-detect';
import { useAppSelector } from 'hooks/redux';
import { forceFinishBroadcast } from 'features/hub/hubThunks';
import { WebSocketContext } from 'features/hub/hub';

export interface PlayerConfigInterface {
  audioSrc: string;
  autoPlay: boolean;
  audioType: 'event' | 'recording';
  withDelay?: boolean;
}

// Allowance for clock skew between the listener and the server when deciding
// whether a scheduled recording has reached its end.
const SCHEDULED_RECORDING_END_GRACE_SECONDS = 30;

export type LoadAudioFunction = (config: PlayerConfigInterface) => any;
export type StopAudioFunction = (resetAudio?: boolean) => any;

export interface AudioPlayerContext {
  audio: HTMLAudioElement | null;
  audioCtx: AudioContext | null;
  volume: number;
  setVolume: (volume: number | ((prevState: number) => number)) => void;
  loadAudio: (config: PlayerConfigInterface) => void;
  unlockAudio: () => void;
  play: () => void;
  stop: (resetAudio?: boolean) => void;
  pause: () => void;
  togglePlayPause: () => void;
  disconnect: () => void;
}

export interface AudioPlayerPositionContext {
  seeking: boolean;
  setSeeking: (seeking: boolean) => void;
  position: number;
  setPosition: (position: number) => void;
}

export const initialState: AudioPlayerContext = {
  audio: null,
  audioCtx: null,
  volume: 1,
  setVolume: () => null,
  play: () => null,
  loadAudio: () => null,
  unlockAudio: () => null,
  stop: () => null,
  pause: () => null,
  togglePlayPause: () => null,
  disconnect: () => null
};

export const AudioPlayerContext =
  createContext<AudioPlayerContext>(initialState);

export const AudioPositionContext = createContext<AudioPlayerPositionContext>({
  seeking: false,
  setSeeking: () => null,
  position: 0,
  setPosition: () => null
});

export const useAudio = () => {
  const context = useContext(AudioPlayerContext);

  if (!context && typeof window !== 'undefined') {
    throw new Error(`useAudio must be used within a AudioContext`);
  }
  return context;
};

export interface AudioPlayerProviderProps {
  children: React.ReactNode;
  value?: AudioPlayerContext;
}

export const AudioPlayerProvider = ({
  children,
  value
}: AudioPlayerProviderProps) => {
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [audioCtx, setAudioCtx] = useState<AudioContext | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [autoPlay, setAutoPlay] = useState<boolean>(false);
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const reconnectRef = useRef<NodeJS.Timeout>();
  const errorRetryRef = useRef<number>(0);
  const audioSetupRef = useRef<boolean>(false);
  const audioUnlockRef = useRef<boolean>(false);
  const primedAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxSetupRef = useRef<boolean>(false);
  const currentBroadcastRef = useRef<Broadcast | null>(null);
  const currentBroadcastFinishedRef = useRef<boolean>(false);
  const currentEventEndsAtRef = useRef<string | null>(null);
  const stallRecoveryRef = useRef<NodeJS.Timeout | null>(null);
  const audioTypeRef = useRef<'event' | 'recording'>('event');
  // Set by the listener effect so teardownAudio can detach handlers without
  // depending on them (the handlers themselves depend on teardownAudio).
  const detachListenersRef = useRef<(() => void) | null>(null);
  const [position, setPosition] = useState<number>(0);
  const [seeking, setSeeking] = useState<boolean>(false);
  const positionContextValue = useMemo(
    () => ({
      seeking,
      setSeeking,
      position,
      setPosition
    }),
    [seeking, setSeeking, position, setPosition]
  );
  const playing = useAppSelector((state) => state.audio.playing);
  const currentEventId = useAppSelector((state) => state.events.currentEventId);
  const currentRecordingId = useAppSelector(
    (state) => state.recordings.currentRecordingId
  );

  const [volume, setVolume] = useLocalStorage('volume', 1);

  const currentEventEndsAt = useAppSelector((state) => {
    const { currentEventId, events, privateEvents } = state.events;
    if (!currentEventId) return null;
    return (
      (events[currentEventId] || privateEvents[currentEventId])?.endsAt ?? null
    );
  });

  const currentBroadcast = useAppSelector(currentEventBroadcastSelector);
  const currentBroadcastFinished = useAppSelector(
    currentBroadcastFinishedSelector
  );
  const { disconnectBroadcast } = useContext(WebSocketContext);

  const silentAudio =
    'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAVFYAAFRWAAABAAgAZGF0YQAAAAA=';

  const unlockAudio = useCallback(() => {
    if (!isSafari) return;
    if (primedAudioRef.current) return;

    console.log('[Audio] - Priming Safari audio element');
    const primedAudio = new Audio();
    primedAudio.crossOrigin = 'anonymous';
    primedAudio.src = silentAudio;
    primedAudio.volume = 0;
    primedAudio.play().catch((err) => {
      if (err?.name !== 'AbortError') {
        console.log('[Audio] - Safari prime play rejected', err);
      }
    });
    primedAudioRef.current = primedAudio;
    audioUnlockRef.current = true;
  }, []);

  const loadAudio = useCallback(
    (config: PlayerConfigInterface) => {
      console.log('[Audio] - Setting config');
      const url =
        import.meta.env.VITE_ENV_NAME === 'development'
          ? config.audioType === 'event'
            ? 'https://radiomeuh2.ice.infomaniak.ch/radiomeuh2-128.mp3'
            : 'https://ilo-voices.cdn.prismic.io/ilo-voices/c336ed2f-9e7f-4303-a426-590a63fd8462_The+Future+of+Work+Podcast+-+Green+Jobs+%28French%29.mp3'
          : config.audioSrc;
      audioTypeRef.current = config.audioType;
      unlockAudio();
      dispatch(
        checkAndPlayAudio(url, config.audioType, setSrc, config.withDelay)
      );
      setAutoPlay(config.autoPlay);
    },
    [dispatch, setAutoPlay, unlockAudio, setSrc]
  );

  const reset = useCallback(
    (resetAudio?: boolean) => {
      setSrc(null);
      setAudio(null);
      setAutoPlay(false);
      audioSetupRef.current = false;

      if (resetAudio) {
        dispatch(resetAudioState());
      } else {
        dispatch(setPlay(false));
        dispatch(setPlaying(false));
      }
    },
    [setSrc, setAudio, setAutoPlay, dispatch]
  );

  // Silence and unload the current element before it is dropped from state.
  // reset() only nulls the state, so without this an element that is still
  // playing (e.g. a stall-triggered reconnect) keeps streaming alongside the
  // new one with nothing left referencing it — double audio / echo.
  const teardownAudio = useCallback(() => {
    if (!audio) return;
    timeoutRef.current && clearTimeout(timeoutRef.current);
    detachListenersRef.current?.();
    detachListenersRef.current = null;
    audio.pause();
    audio.src = silentAudio;
    audio.currentTime = 0;
    primedAudioRef.current = audio;
  }, [audio]);

  const play = useCallback(() => {
    if (!audio) return;
    dispatch(setLoading(true));
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('[Audio] - Play Promise success');
        })
        .catch((error) => {
          console.log('[Audio] - Play Promise error', error);
          // Autoplay could fail because of no user interaction (click)
          // Let the user manually play the audio now
          dispatch(setLoading(false));
          dispatch(setPlaying(false));
          dispatch(setPlay(false));
          audio.pause();
          audio.currentTime = 0;
        });
    }
  }, [audio, dispatch]);

  const pause = useCallback(() => {
    if (audio) {
      audio.pause();
      dispatch(setPlaying(false));
    }
  }, [audio, dispatch]);

  const togglePlayPause = useCallback(() => {
    if (!audio) return;
    if (playing) {
      dispatch(setPlaying(false));
      dispatch(setPlay(false));
      audio.pause();
    } else {
      dispatch(setPlaying(true));
      play();
    }
  }, [audio, dispatch, playing, play]);

  const handleReconnect = useCallback(() => {
    if (!src) return;

    // Capture before teardown: changing src clears audio.error
    const error = audio?.error;

    if (currentRecordingId && !currentEventId) {
      const errorCode = error?.code;
      dispatch(setError(new Error(errorCode ? `${errorCode}` : 'recording')));
      teardownAudio();
      reset(false);
      return;
    }

    if (stallRecoveryRef.current) {
      clearTimeout(stallRecoveryRef.current);
      stallRecoveryRef.current = null;
    }
    if (errorRetryRef.current < 20) {
      errorRetryRef.current++;
      console.log('[Audio] - Reconnecting', errorRetryRef.current);
      teardownAudio();
      reset();
      dispatch(setConnecting(true));
      reconnectRef.current = setTimeout(() => {
        loadAudio({
          audioSrc: src,
          autoPlay: true,
          audioType: audioTypeRef.current
        });
      }, 2000);
    } else {
      teardownAudio();
      reset();
      if (error) {
        dispatch(
          createAlert({
            message: 'Error loading audio',
            type: 'error'
          })
        );
        switch (error.code) {
          case error.MEDIA_ERR_ABORTED:
            console.log('[Audio] - Error - Aborted the video playback.');
            dispatch(setError(new Error(`${error.MEDIA_ERR_ABORTED}`)));
            break;
          case error.MEDIA_ERR_NETWORK:
            console.log(
              '[Audio] - Error - A network error caused the audio download to fail.'
            );
            dispatch(setError(new Error(`${error.MEDIA_ERR_NETWORK}`)));
            break;
          case error.MEDIA_ERR_DECODE:
            console.log(
              '[Audio] - Error - The audio playback was aborted due to a corruption problem or because the video used features your browser did not support.'
            );
            dispatch(setError(new Error(`${error.MEDIA_ERR_DECODE}`)));
            break;
          case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            console.log(
              '[Audio] - Error - The video audio not be loaded, either because the server or network failed or because the format is not supported.'
            );
            dispatch(
              setError(new Error(`${error.MEDIA_ERR_SRC_NOT_SUPPORTED}`))
            );
            break;
          default:
            console.log('[Audio] - Error - An unknown error occurred.');
            break;
        }
      }
    }
  }, [
    audio,
    dispatch,
    reset,
    teardownAudio,
    src,
    loadAudio,
    currentEventId,
    currentRecordingId
  ]);

  const handleStalled = useCallback(() => {
    console.log('[Audio] - Stalling');
    if (stallRecoveryRef.current) clearTimeout(stallRecoveryRef.current);
    stallRecoveryRef.current = setTimeout(() => {
      stallRecoveryRef.current = null;
      if (audio && !audio.paused && audio.readyState < 3) {
        console.log('[Audio] - Stall timeout, reconnecting');
        handleReconnect();
      }
    }, 5000);
  }, [audio, handleReconnect]);

  const handleWaiting = useCallback(() => {
    console.log('[Audio] - Waiting');
  }, []);

  const handleCanPlay = useCallback(() => {
    console.log('[Audio] - Can play through');
    dispatch(setConnecting(false));
    dispatch(setLoading(false));
    dispatch(setReady(true));
  }, [dispatch]);

  const handleEnded = useCallback(() => {
    console.log('[Audio] - Ended');
    dispatch(setAudioEndedTimestamp(dayjs().unix()));

    if (!audio) return;
    if (currentEventId) {
      // A scheduled recording is relayed through the streaming servers, so the
      // element also ends when the source drops off the origin or an edge
      // closes the connection. Only treat it as the recording finishing once
      // the event's scheduled end (starts_at + recording duration) is reached;
      // before that, reconnect like a live broadcast.
      const scheduledRecordingEnded =
        !!currentBroadcastRef.current?.scheduled_recording &&
        !!currentEventEndsAtRef.current &&
        !dayjs().isBefore(
          dayjs(currentEventEndsAtRef.current).subtract(
            SCHEDULED_RECORDING_END_GRACE_SECONDS,
            'second'
          )
        );

      if (currentBroadcastFinishedRef.current || scheduledRecordingEnded) {
        reset(true);

        if (scheduledRecordingEnded) {
          dispatch(
            forceFinishBroadcast(
              currentBroadcastRef.current,
              undefined,
              disconnectBroadcast,
              true
            )
          );
        }
      } else {
        console.log(
          '[Audio] - Ended but broadcast not finished, trying to reconnect'
        );
        if (src) {
          teardownAudio();
          reset();
          dispatch(setConnecting(true));
          reconnectRef.current = setTimeout(() => {
            if (currentBroadcastFinishedRef.current) {
              dispatch(setConnecting(false));
              reset(true);
              return;
            }
            loadAudio({
              audioSrc: src,
              autoPlay: true,
              audioType: 'event'
            });
          }, 5000);
        }
      }
    } else {
      audio.currentTime = 0;
      setSeeking(false);
      setPosition(0);
    }
  }, [
    src,
    loadAudio,
    audio,
    dispatch,
    currentEventId,
    reset,
    teardownAudio,
    disconnectBroadcast
  ]);

  const handleTimeUpdate = useCallback(() => {
    if (stallRecoveryRef.current) {
      clearTimeout(stallRecoveryRef.current);
      stallRecoveryRef.current = null;
    }
    timeoutRef.current && clearTimeout(timeoutRef.current);
    dispatch(setConnecting(false));
    timeoutRef.current = setTimeout(() => {
      if (audio && !audio.paused) {
        dispatch(setConnecting(true));
      }
    }, 3000);
  }, [audio, dispatch]);

  const handlePlay = useCallback(() => {
    console.log('[Audio] - Play');
    dispatch(setPlay(true));
  }, [dispatch]);

  const handlePlaying = useCallback(() => {
    console.log('[Audio] - Playing');
    dispatch(setConnecting(false));
    dispatch(setLoading(false));
    dispatch(setPlaying(true));
    errorRetryRef.current = 0;
    reconnectRef.current && clearTimeout(reconnectRef.current);
    if (stallRecoveryRef.current) {
      clearTimeout(stallRecoveryRef.current);
      stallRecoveryRef.current = null;
    }
  }, [dispatch]);

  const handlePause = useCallback(() => {
    console.log('[Audio] - Paused');
    dispatch(setPlaying(false));
    dispatch(setPlay(false));
  }, [dispatch]);

  const handleError = useCallback(
    (event: ErrorEvent) => {
      console.log('[Audio] - Error', event);
      if (audio && audio.error) {
        handleReconnect();
      }
    },
    [audio, handleReconnect]
  );

  // Mainly used for testing
  const disconnect = useCallback(() => {
    console.log('[Audio] - Disconnecting audio stream');
    handleReconnect();
  }, [handleReconnect]);

  const handleLoadedData = useCallback(() => {
    console.log('[Audio] - Loaded Data');
    if (audio) {
      dispatch(setDuration(audio.duration));
    }
  }, [dispatch, audio]);

  const stop = useCallback(
    (resetAudio: boolean | undefined) => {
      console.log('[Audio] - Stop and reset');

      reconnectRef.current && clearTimeout(reconnectRef.current);
      if (stallRecoveryRef.current) {
        clearTimeout(stallRecoveryRef.current);
        stallRecoveryRef.current = null;
      }

      fetchAudioAbort();

      if (audio) {
        dispatch(setAudioEndedTimestamp(dayjs().unix()));
        teardownAudio();
      }

      reset(resetAudio);
    },
    [dispatch, audio, reset, teardownAudio]
  );

  const stopRef = useRef(stop);
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);
  const stableStop = useCallback<StopAudioFunction>((resetAudio) => {
    return stopRef.current(resetAudio);
  }, []);

  useEffect(() => {
    currentBroadcastRef.current = currentBroadcast;
  }, [currentBroadcast]);

  useEffect(() => {
    currentEventEndsAtRef.current = currentEventEndsAt;
  }, [currentEventEndsAt]);

  useEffect(() => {
    currentBroadcastFinishedRef.current = currentBroadcastFinished;
    if (currentBroadcastFinished && reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = undefined;
    }
  }, [currentBroadcastFinished]);

  useLayoutEffect(() => {
    if (!src) return;
    if (!audioSetupRef.current) {
      let newAudio: HTMLAudioElement;
      if (primedAudioRef.current) {
        console.log('[Audio] - Reusing primed audio element');
        newAudio = primedAudioRef.current;
        primedAudioRef.current = null;
      } else {
        console.log('[Audio] - Setting audio');
        newAudio = new Audio();
        newAudio.crossOrigin = 'anonymous';
      }
      newAudio.src = src;
      newAudio.volume = volume;
      newAudio.load();
      setAudio(newAudio);
      setDuration(0);
      audioSetupRef.current = true;
    }
  }, [src, setAudio, volume]);

  useEffect(() => {
    if (!audio) return;
    if (!audioCtxSetupRef.current) {
      audioCtxSetupRef.current = true;
      // Load the WebAudio engine on demand the first time playback sets up an
      // audio element — keeps standardized-audio-context out of the entry chunk.
      import('standardized-audio-context').then(({ AudioContext }) => {
        setAudioCtx(new AudioContext());
      });
    }
  }, [audio, audioCtx]);

  useEffect(() => {
    if (audio && autoPlay) {
      dispatch(setLoading(true));
      play();
    }
  }, [autoPlay, audio, dispatch, play]);

  useEffect(() => {
    if (audio) {
      audio.volume = volume;
    }
  }, [volume, audio]);

  useEffect(() => {
    if (!audio) return;
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('stalled', handleStalled);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplaythrough', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    // Without this cleanup, every handler re-memoization (handleEnded depends
    // on `src` etc.) attaches a fresh listener to the same audio element and
    // leaves the previous one behind. Stale listeners then fire on the old
    // element when stop() resets src to silentAudio — for example
    // canplaythrough firing immediately and dispatching setLoading(false),
    // which prematurely clears the loading state during fallback navigation.
    const detach = () => {
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('stalled', handleStalled);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplaythrough', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
    };
    detachListenersRef.current = detach;

    return () => {
      detach();
      if (detachListenersRef.current === detach) {
        detachListenersRef.current = null;
      }
    };
  }, [
    audio,
    handleLoadedData,
    handleStalled,
    handleWaiting,
    handleCanPlay,
    handleTimeUpdate,
    handleEnded,
    handlePlay,
    handlePlaying,
    handlePause,
    handleError
  ]);

  const contextValue: AudioPlayerContext = useMemo(() => {
    return value
      ? value
      : {
          loadAudio,
          unlockAudio,
          play,
          pause,
          stop: stableStop,
          audio,
          audioCtx,
          volume,
          setVolume,
          togglePlayPause,
          value,
          disconnect
        };
  }, [
    loadAudio,
    unlockAudio,
    play,
    pause,
    stableStop,
    audio,
    audioCtx,
    volume,
    setVolume,
    togglePlayPause,
    value,
    disconnect
  ]);

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      <AudioPositionContext.Provider value={positionContextValue}>
        {children}
      </AudioPositionContext.Provider>
    </AudioPlayerContext.Provider>
  );
};
